import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Award,
  CheckCircle2,
  ExternalLink,
  Gift,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Users,
} from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import SmartResponseSubNav from './SmartResponseSubNav';
import {
  fetchSRDashboard,
  SRGeo,
  syncSRMetaInsights,
  updateSRGuardianConfig,
} from '../../services/smartResponseApi';

const dkk = (v: number | null | undefined) =>
  v == null ? '–' : `${v.toLocaleString('da-DK', { maximumFractionDigits: 0 })} kr.`;
const dkkDec = (v: number | null | undefined) =>
  v == null ? '–' : `${v.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr.`;

export default function SmartResponseMetaDashboard() {
  const [geo, setGeo] = useState<SRGeo>('no');
  const [days, setDays] = useState<number>(14);
  const [syncingMeta, setSyncingMeta] = useState<boolean>(false);
  const [syncBanner, setSyncBanner] = useState<string | null>(null);

  // Guardian config state
  const [guardianMode, setGuardianMode] = useState<'alert_only' | 'auto_pause'>('alert_only');
  const [maxBleedSpend, setMaxBleedSpend] = useState<number>(100);
  const [maxDupRate, setMaxDupRate] = useState<number>(25);
  const [guardianSavedMsg, setGuardianSavedMsg] = useState<string | null>(null);

  const { data, isFetching, refetch } = useQuery({
    queryKey: ['sr-dashboard', geo, days],
    queryFn: () => fetchSRDashboard(geo, days),
  });

  const s = data?.summary;
  const guardian = data?.guardian;
  const adWinners = data?.ad_winners || [];

  const handleSyncMeta = async () => {
    setSyncingMeta(true);
    setSyncBanner(null);
    try {
      const res = await syncSRMetaInsights(days);
      setSyncBanner(
        `✓ Synkroniseret mod Meta Ad Account ${res.account_id || 'act_995004339585761'} (${
          (res.ads || []).length
        } aktive annoncer hentet).`
      );
      await refetch();
    } finally {
      setSyncingMeta(false);
    }
  };

  const handleSaveGuardian = async () => {
    setGuardianSavedMsg(null);
    await updateSRGuardianConfig({
      mode: guardianMode,
      max_spend_zero_accepted_dkk: maxBleedSpend,
      max_duplicate_rate_pct: maxDupRate,
    });
    setGuardianSavedMsg('✓ Guardian overvågnings- og auto-pause-regler er opdateret.');
    refetch();
  };

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto">
        <SmartResponseSubNav />

        {/* Header & Filter Bar */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6 shadow-sm flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="px-2.5 py-0.5 text-xs font-bold uppercase tracking-wider rounded bg-[#0F3F34] text-white">
                SmartResponse AI Lead Engine
              </span>
              <span className="text-xs text-gray-500 font-medium">
                Meta Instant Forms (act_995004339585761) → 7753/8886 Filter → FeedOrder.aspx (100168/1336) → premieportal.com Offerwall
              </span>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mt-1">
              Live Performance, Annonce-Vindere, Alder×Køn Segmenter & Guardian Alerts
            </h2>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
              {(
                [
                  { id: 'no', label: '🇳🇴 Norge (PremiePortalen)' },
                  { id: 'dk', label: '🇩🇰 Danmark' },
                  { id: 'all', label: 'Alle (NO + DK)' },
                ] as { id: SRGeo; label: string }[]
              ).map((g) => (
                <button
                  key={g.id}
                  onClick={() => setGeo(g.id)}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                    geo === g.id ? 'bg-[#0F3F34] text-white' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {g.label}
                </button>
              ))}
            </div>

            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700"
            >
              <option value={7}>Seneste 7 dage</option>
              <option value={14}>Seneste 14 dage</option>
              <option value={30}>Seneste 30 dage</option>
            </select>

            <button
              onClick={handleSyncMeta}
              disabled={syncingMeta || isFetching}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-xs font-semibold text-gray-700"
            >
              <RefreshCw size={14} className={syncingMeta || isFetching ? 'animate-spin' : ''} />
              Synkronisér Meta Insights
            </button>

            <Link
              to="/smartresponse/campaigns"
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-[#F07824] hover:opacity-95 text-white text-xs font-bold shadow-sm"
            >
              <Sparkles size={14} />
              AI Campaign Builder & Kø
            </Link>
          </div>
        </div>

        {syncBanner && (
          <div className="mb-5 p-3.5 rounded-xl bg-emerald-50 border border-emerald-200 text-xs font-semibold text-emerald-900 flex items-center justify-between">
            <span>{syncBanner}</span>
            <button onClick={() => setSyncBanner(null)} className="text-emerald-700 underline">
              Luk
            </button>
          </div>
        )}

        {/* Zero-Spend Ready Banner when account has 0 spend yet */}
        {s && s.spend_dkk === 0 && s.raw_leads === 0 && (
          <div className="bg-[#F4F8F6] border border-[#0F3F34]/20 rounded-xl p-4 mb-6 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <ShieldCheck size={20} className="text-[#0F3F34] shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-bold text-[#0F3F34]">
                  Klar til første live-kampagne på act_995004339585761 (Ingen falske demo-tal)
                </h3>
                <p className="text-xs text-gray-700 mt-0.5">
                  Alle 0-tal herunder er reelle: Annoncekontoen <code>act_995004339585761</code>, Facebook-siden{' '}
                  <code>PremiePortalen (1420182721178198)</code>, portalen <code>https://premieportal.com</code> og{' '}
                  <code>FeedOrder.aspx (sourceId=100168, partnerId=1336)</code> er forbundet. Vores første kampagneplan{' '}
                  (<strong>NO OneCall Rema 1000 — 114927</strong>) ligger klar i køen under AI Campaign Builder.
                </p>
              </div>
            </div>
            <Link
              to="/smartresponse/campaigns"
              className="px-3.5 py-2 rounded-lg bg-[#0F3F34] text-white text-xs font-bold hover:opacity-95 shrink-0"
            >
              Gå til Kampagnekø (OneCall Rema 1000) →
            </Link>
          </div>
        )}

        {/* 6 KPI Cards */}
        {s && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <p className="text-xs font-semibold text-gray-500 uppercase">Ad Spend (Meta)</p>
              <p className="text-xl font-extrabold text-gray-900 mt-1">{dkk(s.spend_dkk)}</p>
              <p className="text-xs text-gray-500 mt-1">Brutto CPL: {dkkDec(s.gross_cpl_dkk)}</p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <p className="text-xs font-semibold text-gray-500 uppercase">Godkendte Leads (SR)</p>
              <p className="text-xl font-extrabold text-[#0F3F34] mt-1">
                {s.sr_api_accepted.toLocaleString('da-DK')}{' '}
                <span className="text-xs font-medium text-gray-400">/ {s.raw_leads} rå</span>
              </p>
              <p className="text-xs text-emerald-700 font-medium mt-1">
                Netto CPL: {dkkDec(s.net_accepted_cpl_dkk)}
              </p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <p className="text-xs font-semibold text-gray-500 uppercase">Dublet / Filter Rate</p>
              <p className="text-xl font-extrabold text-amber-700 mt-1">{s.duplicate_rate_pct}%</p>
              <p className="text-xs text-gray-500 mt-1">
                {s.pre_dedup_blocked} pre-filter • {s.sr_api_duplicates} SR dup
              </p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <p className="text-xs font-semibold text-gray-500 uppercase">Direkte SR Indtægt</p>
              <p className="text-xl font-extrabold text-gray-900 mt-1">{dkk(s.primary_revenue_dkk)}</p>
              <p className="text-xs text-gray-500 mt-1">FeedOrder.aspx (15m sync)</p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <p className="text-xs font-semibold text-gray-500 uppercase">Offerwall Krydssalg</p>
              <p className="text-xl font-extrabold text-[#F07824] mt-1">{dkk(s.offerwall_revenue_dkk)}</p>
              <p className="text-xs text-gray-500 mt-1">
                {s.offerwall_clicks} klik → {s.offerwall_conversions} ekstra leads
              </p>
            </div>

            <div className="bg-[#0F3F34] text-white rounded-xl p-4 shadow-sm">
              <p className="text-xs font-semibold text-emerald-200 uppercase">Netto Profit & ROAS</p>
              <p className="text-xl font-extrabold mt-1">{dkk(s.net_profit_dkk)}</p>
              <p className="text-xs text-emerald-200 font-semibold mt-1">
                Total ROAS: {s.roas.toFixed(2)}x ({dkk(s.total_revenue_dkk)})
              </p>
            </div>
          </div>
        )}

        {/* Guardian Alerts & Infrastructure Health Check */}
        {guardian && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
            <div className="lg:col-span-7 bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={18} className="text-[#0F3F34]" />
                  <h3 className="text-base font-bold text-gray-900">
                    System-Helbred & Live Tracking-Forbindelser
                  </h3>
                </div>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-50 text-emerald-800 border border-emerald-200">
                  {guardian.alerts_count === 0 ? '0 Aktive Alarmer' : `${guardian.alerts_count} Alarmer`}
                </span>
              </div>

              <div className="space-y-2.5">
                {guardian.health_checks.map((hc) => (
                  <div
                    key={hc.key}
                    className="flex items-start justify-between gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200 text-xs"
                  >
                    <div className="flex items-start gap-2.5">
                      {hc.status === 'ok' ? (
                        <CheckCircle2 size={15} className="text-emerald-600 shrink-0 mt-0.5" />
                      ) : (
                        <AlertTriangle size={15} className="text-amber-600 shrink-0 mt-0.5" />
                      )}
                      <div>
                        <p className="font-bold text-gray-900">{hc.label}</p>
                        <p className="text-gray-600 mt-0.5">{hc.detail}</p>
                      </div>
                    </div>
                    <span
                      className={`px-2 py-0.5 rounded font-bold uppercase text-[10px] shrink-0 ${
                        hc.status === 'ok'
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-amber-100 text-amber-800'
                      }`}
                    >
                      {hc.status === 'ok' ? 'VERIFICERET' : 'KLAR (DRY-RUN)'}
                    </span>
                  </div>
                ))}
              </div>

              {guardian.alerts.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-xs font-bold uppercase text-red-700">Aktive Guardian Alarmer</p>
                  {guardian.alerts.map((al, i) => (
                    <div
                      key={i}
                      className="p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-900"
                    >
                      <p className="font-bold">{al.title}</p>
                      <p className="mt-0.5">{al.message}</p>
                      <p className="mt-1 font-semibold text-red-700">Handling: {al.recommended_action}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Guardian Rule Controls */}
            <div className="lg:col-span-5 bg-white rounded-xl border border-gray-200 p-5 shadow-sm flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <ShieldAlert size={18} className="text-[#F07824]" />
                  <h3 className="text-base font-bold text-gray-900">
                    Guardian Alerts & Auto-Pause Regler
                  </h3>
                </div>
                <p className="text-xs text-gray-500 mb-4">
                  Ligesom på SimplyLoan overvåger Guardian-motoren hvert Ad Set på{' '}
                  <code>act_995004339585761</code> og slår alarm (eller pauser), hvis et segment brænder
                  budget uden godkendte <code>FeedOrder.aspx</code> leads.
                </p>

                <div className="space-y-3 text-xs">
                  <div>
                    <label className="block font-semibold text-gray-700 mb-1">Guardian Tilstand</label>
                    <select
                      value={guardianMode}
                      onChange={(e) => setGuardianMode(e.target.value as 'alert_only' | 'auto_pause')}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 font-semibold text-gray-900"
                    >
                      <option value="alert_only">Alert Only (Rapportér alarmer uden at pause automatisk)</option>
                      <option value="auto_pause">Auto-Pause (Pause automatisk blødende Ad Sets i Meta)</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block font-semibold text-gray-700 mb-1">
                        Max Spend m/ 0 SR-Leads (DKK)
                      </label>
                      <input
                        type="number"
                        value={maxBleedSpend}
                        onChange={(e) => setMaxBleedSpend(Number(e.target.value))}
                        className="w-full rounded-lg border border-gray-300 px-3 py-1.5 font-semibold"
                      />
                    </div>
                    <div>
                      <label className="block font-semibold text-gray-700 mb-1">
                        Alarm ved Dublet-rate over (%)
                      </label>
                      <input
                        type="number"
                        value={maxDupRate}
                        onChange={(e) => setMaxDupRate(Number(e.target.value))}
                        className="w-full rounded-lg border border-gray-300 px-3 py-1.5 font-semibold"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-gray-100">
                <button
                  onClick={handleSaveGuardian}
                  className="w-full px-4 py-2 rounded-lg bg-[#0F3F34] hover:opacity-95 text-white text-xs font-bold"
                >
                  Gem Guardian Regler
                </button>
                {guardianSavedMsg && (
                  <p className="text-xs font-semibold text-emerald-700 mt-2">{guardianSavedMsg}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Ad & Creative Winners (From Meta Insights API) */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <div className="flex items-center gap-2">
              <Award size={18} className="text-[#F07824]" />
              <h3 className="text-base font-bold text-gray-900">
                Annonce- & Kreativ-Vindere (Ad-Level fra Meta Insights + FeedOrder.aspx)
              </h3>
            </div>
            <span className="text-xs text-gray-500">
              Kilde: <code>GET /act_995004339585761/insights?level=ad</code> koblet med SR Lead-ledger
            </span>
          </div>

          {adWinners.length === 0 ? (
            <div className="p-6 rounded-xl bg-gray-50 border border-dashed border-gray-200 text-center">
              <p className="text-sm font-bold text-gray-800">
                Ingen aktive annoncer har brugt budget på act_995004339585761 i perioden endnu
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Når vores første kampagne (<strong>NO OneCall Rema 1000 — 114927</strong>) aktiveres, vises hvert
                kreativ og Ad Set her rangeret efter laveste Netto CPL og højeste ROAS.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-gray-200 text-[11px] font-bold uppercase text-gray-500">
                    <th className="py-2.5 pr-3">Annonce (Ad)</th>
                    <th className="py-2.5 px-3">Ad Set (Segment)</th>
                    <th className="py-2.5 px-3 text-right">Spend</th>
                    <th className="py-2.5 px-3 text-right">Klik (CTR)</th>
                    <th className="py-2.5 px-3 text-right">Meta Leads</th>
                    <th className="py-2.5 px-3 text-right">SR Godkendt</th>
                    <th className="py-2.5 px-3 text-right">Netto CPL</th>
                    <th className="py-2.5 pl-3 text-right">ROAS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-xs">
                  {adWinners.map((ad, idx) => (
                    <tr key={ad.ad_id || idx} className="hover:bg-gray-50">
                      <td className="py-2.5 pr-3 font-bold text-gray-900">{ad.ad_name}</td>
                      <td className="py-2.5 px-3 text-gray-600">{ad.adset_name || '–'}</td>
                      <td className="py-2.5 px-3 text-right font-medium">{dkkDec(ad.spend_dkk)}</td>
                      <td className="py-2.5 px-3 text-right">
                        {ad.clicks} <span className="text-gray-400">({ad.ctr_pct}%)</span>
                      </td>
                      <td className="py-2.5 px-3 text-right font-semibold">{ad.meta_leads}</td>
                      <td className="py-2.5 px-3 text-right font-bold text-emerald-700">
                        {ad.sr_accepted_leads}
                      </td>
                      <td className="py-2.5 px-3 text-right font-semibold">{dkkDec(ad.net_cpl_dkk)}</td>
                      <td className="py-2.5 pl-3 text-right font-bold text-[#0F3F34]">
                        {ad.roas.toFixed(2)}x
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Demographic Breakdown: Age Brackets + Gender */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
          <div className="lg:col-span-8 bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Users size={18} className="text-[#0F3F34]" />
                <h3 className="text-base font-bold text-gray-900">
                  Alders-Segmentering (Tilladt fordi special_ad_categories = [])
                </h3>
              </div>
              <span className="text-xs text-gray-500">Felt 117/119 Fødselsår + Meta Age Breakdown</span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-gray-200 text-[11px] font-bold uppercase text-gray-500">
                    <th className="py-2.5 pr-3">Aldersgruppe</th>
                    <th className="py-2.5 px-2 text-right">Spend</th>
                    <th className="py-2.5 px-2 text-right">Rå Leads</th>
                    <th className="py-2.5 px-2 text-right">SR Godkendt</th>
                    <th className="py-2.5 px-2 text-right">Netto CPL</th>
                    <th className="py-2.5 px-2 text-right">ROAS</th>
                    <th className="py-2.5 pl-3">Strategi / Kvalificeringsregel</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-xs">
                  {(data?.by_age_segment || []).map((row) => (
                    <tr key={row.age_bucket} className="hover:bg-gray-50">
                      <td className="py-3 pr-3 font-bold text-gray-900">{row.age_bucket}</td>
                      <td className="py-3 px-2 text-right text-gray-700">{dkk(row.spend_dkk)}</td>
                      <td className="py-3 px-2 text-right text-gray-700">{row.raw_leads}</td>
                      <td className="py-3 px-2 text-right font-semibold text-emerald-700">
                        {row.accepted_leads}
                      </td>
                      <td className="py-3 px-2 text-right font-semibold text-gray-900">
                        {dkkDec(row.net_accepted_cpl_dkk)}
                      </td>
                      <td className="py-3 px-2 text-right font-bold text-[#0F3F34]">
                        {row.roas.toFixed(2)}x
                      </td>
                      <td className="py-3 pl-3 text-gray-600">{row.recommendation}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="lg:col-span-4 bg-white rounded-xl border border-gray-200 p-5 shadow-sm flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp size={18} className="text-[#F07824]" />
                <h3 className="text-base font-bold text-gray-900">
                  Køns-Segmentering (Kvinder vs. Mænd)
                </h3>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Modsat SimplyLoan kan vi her oprette dedikerede Ad Sets til Kvinder (<code>genders=[2]</code>,{' '}
                <code>118=241</code>) og Mænd (<code>genders=[1]</code>, <code>118=240</code>).
              </p>

              <div className="space-y-3">
                {(data?.by_gender || []).map((g) => (
                  <div key={g.gender} className="rounded-lg border border-gray-200 bg-gray-50 p-3.5">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-gray-900">{g.gender}</span>
                      <span className="text-xs font-bold text-[#0F3F34]">
                        {g.accepted_leads || 0} godkendte leads ({g.share_pct}% andel)
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                      <div>
                        <p className="text-gray-500">Spend</p>
                        <p className="font-bold text-gray-900">{dkkDec(g.spend_dkk || 0)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Netto CPL</p>
                        <p className="font-bold text-gray-900">{dkkDec(g.net_accepted_cpl_dkk)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">ROAS</p>
                        <p className="font-bold text-emerald-700">{g.roas.toFixed(2)}x</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4 p-3 rounded-lg bg-[#F4F8F6] border border-emerald-200 text-xs text-gray-700">
              <span className="font-bold text-[#0F3F34]">Tip til Vinder-skalering:</span> I AI Campaign
              Builder kan du slå <strong>"Split Ad Sets på Køn (Kvinder vs. Mænd)"</strong> til, så Meta ikke
              bruger hele budgettet på ét køn uden at vi kan måle CPL separat.
            </div>
          </div>
        </div>

        {/* Competition Performance Table */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <div className="flex items-center gap-2">
              <Gift size={18} className="text-[#0F3F34]" />
              <h3 className="text-base font-bold text-gray-900">
                Aktive Konkurrencer, Kvalificeringspuljer & Krydssalgs-Økonomi
              </h3>
            </div>
            <Link
              to="/smartresponse/portal"
              className="inline-flex items-center gap-1 text-xs font-bold text-[#0F3F34] hover:underline"
            >
              Se fuld AI Kontekst & FeedOrder dokumentation <ExternalLink size={13} />
            </Link>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-200 text-[11px] font-bold uppercase text-gray-500">
                  <th className="py-2.5 pr-3">Konkurrence & SR productId</th>
                  <th className="py-2.5 px-2">Tilknyttet Dubletpulje</th>
                  <th className="py-2.5 px-2 text-right">Payout</th>
                  <th className="py-2.5 px-2 text-right">Spend</th>
                  <th className="py-2.5 px-2 text-right">Godkendte Leads</th>
                  <th className="py-2.5 px-2 text-right">Netto CPL</th>
                  <th className="py-2.5 px-2 text-right">Direkte Rev</th>
                  <th className="py-2.5 px-2 text-right">Offerwall Rev</th>
                  <th className="py-2.5 pl-2 text-right">Netto Profit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-xs">
                {(data?.by_competition || []).map((c) => (
                  <tr key={c.comp_key} className="hover:bg-gray-50">
                    <td className="py-3 pr-3">
                      <div className="flex items-center gap-1.5">
                        <span className="uppercase font-bold px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 text-[10px]">
                          {c.geo}
                        </span>
                        <span className="font-bold text-gray-900">{c.prize_name}</span>
                        {c.comp_key === 'no_onecall_rema1000_114927' && (
                          <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold text-[10px]">
                            #1 LAUNCH
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-gray-400 font-mono mt-0.5">
                        {c.comp_key} • productId={c.sr_campaign_id}
                      </p>
                    </td>
                    <td className="py-3 px-2">
                      <div className="flex flex-wrap gap-1">
                        {c.exclusion_pools.map((p) => (
                          <span
                            key={p}
                            className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 font-mono text-[10px]"
                          >
                            {p}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="py-3 px-2 text-right font-semibold text-gray-700">
                      {dkkDec(c.payout_dkk)}
                    </td>
                    <td className="py-3 px-2 text-right font-medium text-gray-900">
                      {dkk(c.spend_dkk)}
                    </td>
                    <td className="py-3 px-2 text-right">
                      <span className="font-bold text-emerald-700">{c.sr_api_accepted}</span>
                      <span className="text-gray-400"> / {c.raw_leads}</span>
                    </td>
                    <td className="py-3 px-2 text-right font-semibold text-gray-900">
                      {dkkDec(c.net_accepted_cpl_dkk)}
                    </td>
                    <td className="py-3 px-2 text-right text-gray-700">
                      {dkk(c.primary_revenue_dkk)}
                    </td>
                    <td className="py-3 px-2 text-right font-semibold text-[#F07824]">
                      +{dkk(c.offerwall_revenue_dkk)}
                    </td>
                    <td className="py-3 pl-2 text-right font-bold text-[#0F3F34]">
                      {dkk(c.net_profit_dkk)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
